/*
 *  -----------------------------------------------------------------------
 *    <copyright file="companion-view/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tabs } from '@mui/material';
import Tab from '@mui/material/Tab';
import { orderBy } from '@progress/kendo-data-query';
import './styles.scss';
import CustomMenuDetails from '@components/central-monitoring/companion-view/risk-details/custom-menu/index';
import { IActionItemV2 } from '../../core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { ExtraMenuItemPosition } from '../../core/grid-header/grid-menu/grid-menu.interface';
import { ObsRowAction } from '../observations-grid-v2.interface';
import { CompanionViewProps } from './companion-view.interface';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { useTranslation } from 'hooks';
import { GET_CM_OBSERVATIONS_AND_ACTIONS, GET_DATA_REVIEW_TABLES } from 'queries/central-monitoring';
import {
    CentralMonitoringActionFields,
    CentralMonitoringObservationAndActionFields,
} from 'queries/generated/fragments.generated';
import { GET_SITES_DELETE } from 'queries/sites';
import { DashboardType } from 'types/graphql-global-types';
import { filterByType, getPanelName } from 'utils/cm-observations.service';
import { ViewTable } from 'views/components/admin/studies/data-review-tables/preview-table';
import SecondaryPanels from 'views/components/central-monitoring/companion-view/secondary-panels';
import ObservationsGridV2 from 'views/components/central-monitoring/observations-grid-v2';
import { CustomDialog, DialogTitle, DialogContent } from 'views/components/core/dialog';
import ItemWrapper from 'views/components/core/item-wrapper';
import Loading from 'views/components/core/loading';
import { AllRoutes } from '@utils/router/all-routes';

const CompanionView = ({
    open,
    onClose,
    currentPanel,
    panels,
    study,
    selectedSite,
    studyData,
    datasetDate,
    onOpenObservationsModal,
    onOpenActionModal,
    type,
    canEdit,
    datasetDates,
    dataTables,
    sitesStatusesToExclude,
    owners,
    onOpenAddPanelDialog,
    handleDeleteObs,
    handleRestoreObs,
}: CompanionViewProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [loadCurrentTables, { data: currentDataTables }] = useLazyQuery(GET_DATA_REVIEW_TABLES);
    const { data, loading } = useQuery(GET_CM_OBSERVATIONS_AND_ACTIONS, {
        variables: {
            studyId: study?.id,
            siteId: type === DashboardType.SITE ? selectedSite?.id : undefined,
            ...(datasetDate?.isCMR ? { timestamp: datasetDate.created } : {}),
        },
    });

    const [savedData, setSavedData] = useState(null);
    const [active, setActive] = useState(0);
    const [currentTable, setCurrentTable] = useState(null);
    const [observations, setObservations] = useState([]);
    const [menuOptions, setMenuOptions] = useState([]);
    const [ownerOptions, setOwnerOptions] = useState([]);
    const [parentPanel] = useState({ ...currentPanel });
    const { data: siteDeletedData } = useQuery(GET_SITES_DELETE, {
        variables: {
            studyId: study?.id,
        },
    });

    useEffect(() => {
        if (study?.id) loadCurrentTables({ variables: { studyId: study.id } });
    }, [study, datasetDate]);

    useEffect(() => {
        if ((dataTables || currentDataTables?.dataReviewTablesForStudy) && parentPanel) {
            let tables = [...(dataTables ?? currentDataTables?.dataReviewTablesForStudy ?? [])]
                // eslint-disable-next-line array-callback-return
                .map((item: any) => {
                    const element = parentPanel.dataReviews?.find((el: any) => el.dataReviewId === item.id);
                    if (element)
                        return {
                            ...item,
                            index: element.index,
                        };
                })
                .filter((item: any) => item && item.active);
            tables = orderBy(tables, [{ field: 'index', dir: 'asc' }]);
            setMenuOptions(
                tables.map((item: any) => ({
                    label: item.name,
                    id: item.id,
                    onClick: () => {
                        setSavedData(null);
                        setCurrentTable(item);
                    },
                })),
            );
            setCurrentTable(tables[0]);
        }
    }, [dataTables, currentDataTables, parentPanel]);

    useEffect(() => {
        if (owners.length) setOwnerOptions([...owners]);
    }, [owners]);

    useEffect(() => {
        if (data?.centralMonitoringObservations && studyData && currentPanel) {
            const array = data.centralMonitoringObservations

                .filter((observation: CentralMonitoringObservationAndActionFields) =>
                    filterByType(observation, 'companion', datasetDate, datasetDates, currentPanel),
                )
                .map((observation: CentralMonitoringObservationAndActionFields) => {
                    const _activeActions = observation.actions?.filter(
                        (action: CentralMonitoringActionFields) => !action.deleted,
                    );
                    const _recentActionDate = _activeActions
                        .map((action: any) => action.requestDate)
                        .sort()
                        .reverse()[0];
                    const numberOfOpenActions = _activeActions.filter((action: any) => !action.completedDate);
                    const owner: any = observation.ownerId
                        ? ownerOptions.find((el: any) => el.id === observation.ownerId)
                        : null;
                    let _site = studyData?.sites?.find((item: any) => item.Fields['Site.id'] === observation.siteId);
                    if (siteDeletedData?.sitesDelete?.length > 0) {
                        if (_site == null) {
                            _site = siteDeletedData?.sitesDelete?.find((site: any) => site.id === observation.siteId);
                            if (_site) {
                                // The _site property comes from a graphQL query here, and it's immutable and will error if any properties are changed
                                // Make a shallow copy and add the Fields and Deleted properties
                                _site = {
                                    ..._site,
                                    Fields: {
                                        'Site.name': _site.name,
                                        'Site.id': _site.id,
                                        'Site.status': _site.status,
                                    },
                                    Deleted: true,
                                };
                            }
                        } else {
                            const isSiteDeleted = siteDeletedData?.sitesDelete?.find(
                                (site: any) => site.id === _site?.Fields['Site.id'],
                            );
                            // This comes from local state, so while changing it here won't change the local state, we can mutate it here unlike above
                            if (isSiteDeleted) _site.Deleted = true;
                        }
                    }
                    return {
                        ...observation,
                        confirmedCause: observation.confirmedCause ?? '',
                        comments: observation.comments ?? '',
                        potentialCause: observation.potentialCause ?? '',
                        _recentActionDate: _recentActionDate ? new Date(_recentActionDate) : null,
                        _openAction: numberOfOpenActions.length,
                        _numberOfActions: _activeActions?.length,
                        _owner: owner,
                        _ownerLabel: owner?.text ?? '',
                        _datasetDate: observation.dataSet?.dataCutDate ?? null,
                        _panelName: getPanelName(observation.panel, observation.type),
                        _isClosedDateReadOnly:
                            observation.actions?.findIndex((el: any) => !el.completedDate && !el.deleted) >= 0,
                        _site: _site?.Fields['Site.name'] ?? '',
                        _siteId: _site?.Fields['Site.id'] ?? null,
                        _siteStatus: _site?.Fields['Site.status'] ?? null,
                        _siteDeleted: _site?.Deleted ?? false,
                        ...(observation.deleted && observation.audit
                            ? {
                                  _deleteReason: observation.audit.reason,
                                  _deleteDate: observation.audit.created,
                                  _deletedBy: `${observation.audit.userFirstName} ${observation.audit?.userLastName}`,
                              }
                            : {}),
                        actions: observation.actions.map((action: CentralMonitoringActionFields) => {
                            const actionOwner: any = action.ownerId
                                ? ownerOptions.find((el: any) => el.id === action.ownerId)
                                : null;
                            return {
                                ...action,
                                observationId: observation.id,
                                _obsDeleted: observation.deleted,
                                _owner: actionOwner,
                                _site: _site?.Fields['Site.name'] ?? '',
                                _siteId: _site?.Fields['Site.id'] ?? null,
                                _siteStatus: _site?.Fields['Site.status'] ?? null,
                                _siteDeleted: _site?.Deleted ?? false,
                                ownerLabel: actionOwner?.text || '',
                                _isCompletedDateReadOnly: Boolean(observation.closedDate),
                                ...(action.deleted && action.audit
                                    ? {
                                          _deleteReason: action.audit?.reason,
                                          _deleteDate: action.audit?.created,
                                          _deletedBy: `${action.audit?.userFirstName} ${action.audit?.userLastName}`,
                                      }
                                    : {}),
                            };
                        }),
                    };
                });
            setObservations(array);
        }
    }, [data, studyData, ownerOptions, currentPanel, datasetDate, type, datasetDates, siteDeletedData]);

    const close = () => {
        setCurrentTable(null);
        setActive(0);
        setMenuOptions([]);
        onClose();
    };

    const rowActions: ObsRowAction = (item) => {
        if (item.deleted) {
            return [
                {
                    label: 'cm:restoreObservation',
                    icon: ['fal', 'undo'],
                    onClick: () => handleRestoreObs(item),
                    enabled: canEdit && !datasetDate?.isCMR,
                },
            ];
        }

        return [
            {
                label: 'ra:editObservation',
                icon: ['fal', 'edit'],
                onClick: () => onOpenObservationsModal?.(item),
                enabled: canEdit && !datasetDate?.isCMR,
            },
            {
                label: 'ra:assignAction',
                icon: ['fal', 'link'],
                onClick: () => onOpenActionModal?.(null, item),
                enabled: canEdit && !item.closedDate && !datasetDate?.isCMR,
            },
            {
                label: 'cm:deleteObservation',
                icon: ['fal', 'trash'],
                onClick: () => handleDeleteObs(item),
                enabled: canEdit && !datasetDate?.isCMR,
            },
            ...(type === DashboardType.STUDY
                ? [
                      {
                          label: 'cm:viewSiteDashboard',
                          icon: ['far', 'eye'],
                          onClick: () => {
                              close();
                              NavigationService.setViewLegacySiteDashboard();
                              navigate(AllRoutes.path('cm-site-dashboard') as string, {
                                  state: {
                                      skipSelectDate: true,
                                      showDashboard: true,
                                      siteId: item._siteId as string | null | undefined,
                                  },
                              });
                          },
                          enabled:
                              item._siteDeleted === false && item?._siteId
                                  ? (sitesStatusesToExclude?.indexOf(item._siteStatus) < 0 ?? true)
                                  : false,
                      } as IActionItemV2,
                  ]
                : []),
        ];
    };

    if (loading) return <Loading float />;

    return (
        <CustomDialog className="full-screen-dialog" onClose={close} fullWidth scroll="paper" maxWidth="lg" open={open}>
            <DialogTitle id="companion-view-add-action-modal-title">
                {t('admin:companionViewModal', {
                    plotTitle: currentPanel?.graph?.plotTitle,
                    name: type === DashboardType.SITE ? selectedSite?.name : study?.studyNumber,
                })}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <div className="tab-menu-wrapper">
                <Tabs value={active} className="details-tabs" onChange={(e: any, value: any) => setActive(value)}>
                    <Tab label={t('admin:visualizations')} data-testid="visualizations-tab" />
                    <Tab label={t('admin:dataTables')} data-testid="data-tables-tab" />
                </Tabs>
                {active === 1 && (
                    <div className="menu">
                        <CustomMenuDetails menuOptions={menuOptions} noDataMessage={t('noResults')} />
                    </div>
                )}
            </div>
            <DialogContent style={{ padding: '0 10px' }}>
                {active === 0 ? (
                    <SecondaryPanels
                        siteId={type === DashboardType.SITE ? selectedSite?.id : null}
                        currentPanel={currentPanel}
                        panels={panels}
                        sitesStatusesToExclude={sitesStatusesToExclude}
                        onOpenAddPanelDialog={onOpenAddPanelDialog}
                    />
                ) : (
                    <ItemWrapper title={currentTable?.name} hideItemNumber>
                        <span className="data-table-description">{currentTable?.description}</span>
                        {currentTable ? (
                            <ViewTable
                                dataSetId={datasetDate.id}
                                currentTable={currentTable}
                                study={study}
                                sitesStatusesToExclude={sitesStatusesToExclude}
                                siteId={type === DashboardType.SITE ? selectedSite?.id : null}
                                savedData={savedData}
                                setSavedData={setSavedData}
                                onClose={close}
                            />
                        ) : (
                            <h3>Please select a data table.</h3>
                        )}
                    </ItemWrapper>
                )}
                <div className="grid-wrapper grid-wrapper--pb-10">
                    <ObservationsGridV2
                        title={t('ra:observationsAndActions')}
                        observations={
                            (selectedSite && type === DashboardType.SITE) || type === DashboardType.STUDY
                                ? observations
                                : []
                        }
                        study={{ ...study, members: [...owners] }}
                        isCMR={Boolean(datasetDate?.isCMR)}
                        detailClass="biggerWidth"
                        rowActions={rowActions}
                        canEdit={canEdit}
                        type={type}
                        hideCheckbox
                        extraMenuItems={[
                            {
                                position: ExtraMenuItemPosition.expandMenu,
                                customClassName: 'grid-header__item--always-visible',
                                expandMenuItems: [
                                    {
                                        label: 'ra:addObservation',
                                        icon: ['fal', 'plus'],
                                        onClick: () => onOpenObservationsModal?.(),
                                        enabled: canEdit && !datasetDate?.isCMR,
                                    },
                                ],
                            },
                        ]}
                        onClose={close}
                    />
                </div>
            </DialogContent>
        </CustomDialog>
    );
};

export default CompanionView;
